/* eslint-disable no-undef */

import {
  fetchOAuthBridgeTokenOnce,
  generateOAuthBridgeKey,
  getAuthDialogStartUrl,
  getStoredToken,
  setStoredToken,
} from "../helpers/auth-helper";
import {
  detectOutlookHostKind,
  getDialogCloseToComposeDelayMs,
  isMobileOutlookHostKind,
} from "../helpers/outlook-host";

const ROAMING_CONSENT_KEY = "sunnysign_consent_given";

export function traceLog(...args: unknown[]): void {
  console.log("[SunnySign trace]", ...args);
}

export function isMobileOutlookHost(): boolean {
  return isMobileOutlookHostKind(detectOutlookHostKind());
}

export function getOutlookHostName(): string {
  try {
    return Office.context.mailbox?.diagnostics?.hostName ?? "";
  } catch {
    return "";
  }
}

export function isWebOrNewOutlookHost(): boolean {
  const hn = getOutlookHostName();
  return hn === "OutlookWebApp" || hn === "newOutlookWindows";
}

export { isLikelySafariBrowser, isOutlookWebAppInDesktopSafari } from "../helpers/safari-owa";

export { detectOutlookHostKind, isMobileOutlookHostKind } from "../helpers/outlook-host";

export function ensureOfficeReady(callback: () => void): void {
  try {
    if (Office.context?.mailbox) {
      callback();
      return;
    }
  } catch {
    /* continue */
  }
  if (typeof Office !== "undefined" && typeof Office.onReady === "function") {
    Office.onReady(() => callback());
    return;
  }
  callback();
}

export function openMobileOAuthDialogForLaunch(onTokenOrNull: (token: string | null) => void): void {
  if (!Office.context?.ui?.displayDialogAsync) {
    traceLog("displayDialogAsync nicht verfügbar");
    onTokenOrNull(null);
    return;
  }
  let requireConsent = true;
  try {
    const rs = Office.context.roamingSettings;
    if (rs?.get(ROAMING_CONSENT_KEY) === "true") requireConsent = false;
  } catch {
    /* ignore */
  }
  const oauthBridgeKey = generateOAuthBridgeKey();
  const url = getAuthDialogStartUrl(requireConsent, oauthBridgeKey);
  traceLog("Mobil OAuth-Dialog", url);
  let finished = false;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  let bridgePollInFlight = false;
  const finishOnce = (token: string | null, expiresIn?: number) => {
    if (finished) return;
    finished = true;
    if (pollTimer != null) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
    if (token) {
      try {
        setStoredToken(token, expiresIn);
      } catch {
        /* ignore */
      }
    }
    /*
     * Nach Schließen des OAuth-Dialogs: Compose-APIs kurz warten (iOS-Callbacks).
     * Android: längere Pause, damit die Mail-Oberfläche nicht im Hintergrund bleibt (office-js#6052).
     */
    if (token) {
      const ms = getDialogCloseToComposeDelayMs(detectOutlookHostKind());
      setTimeout(() => onTokenOrNull(token), ms);
    } else {
      onTokenOrNull(token);
    }
  };
  Office.context.ui.displayDialogAsync(url, { height: 60, width: 30, promptBeforeOpen: false }, (asyncResult) => {
    if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
      traceLog("displayDialogAsync Start fehlgeschlagen", asyncResult.error);
      finishOnce(null);
      return;
    }
    const dialog = asyncResult.value;
    const pollStart = Date.now();
    const pollMaxMs = 120000;
    pollTimer = setInterval(() => {
      if (finished) return;
      if (Date.now() - pollStart > pollMaxMs) return;
      const t = getStoredToken();
      if (t) {
        traceLog("Mobil OAuth: Token im gemeinsamen Speicher (Fallback zu messageParent)");
        try {
          dialog.close();
        } catch {
          /* ignore */
        }
        finishOnce(t);
        return;
      }
      if (bridgePollInFlight) return;
      bridgePollInFlight = true;
      void fetchOAuthBridgeTokenOnce(oauthBridgeKey)
        .then((bridge) => {
          if (finished || !bridge?.access_token) return;
          traceLog("Mobil OAuth: Token von API-Brücke (iOS-Fallback)");
          try {
            dialog.close();
          } catch {
            /* ignore */
          }
          finishOnce(bridge.access_token, bridge.expires_in);
        })
        .finally(() => {
          bridgePollInFlight = false;
        });
    }, 400);
    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
      try {
        if (!arg || !("message" in arg)) return;
        const raw = arg.message;
        const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (msg?.type === "sunnysign_launch_token") {
          try {
            dialog.close();
          } catch {
            /* ignore */
          }
          if (msg.success && msg.tokenViaBridge) {
            void fetchOAuthBridgeTokenOnce(oauthBridgeKey).then((bridge) => {
              if (bridge?.access_token) {
                finishOnce(bridge.access_token, bridge.expires_in);
              } else {
                finishOnce(null);
              }
            });
          } else if (msg.success && msg.access_token) {
            finishOnce(msg.access_token, typeof msg.expires_in === "number" ? msg.expires_in : undefined);
          } else {
            finishOnce(null);
          }
        }
      } catch (pe) {
        const note = arg && "message" in arg ? String((arg as { message: string }).message ?? "") : "";
        traceLog("DialogMessageReceived parse", pe, note);
      }
    });
    dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
      if (!arg || !("error" in arg)) return;
      const errCode = (arg as { error: number }).error;
      if (errCode === 12006 || errCode === 12003) {
        try {
          dialog.close();
        } catch {
          /* ignore */
        }
        const fallback = getStoredToken();
        if (fallback) {
          traceLog("Mobil OAuth: Token nach Dialog-Ende aus Speicher");
          finishOnce(fallback);
          return;
        }
        void fetchOAuthBridgeTokenOnce(oauthBridgeKey).then((bridge) => {
          if (finished) return;
          if (bridge?.access_token) {
            traceLog("Mobil OAuth: Token nach Dialog-Ende von API-Brücke");
            finishOnce(bridge.access_token, bridge.expires_in);
          } else {
            finishOnce(null);
          }
        });
      }
    });
  });
}

/**
 * Stilles OfficeRuntime-SSO kann im Classic-Launch-Runtime ohne Callback hängen bleiben
 * (z. B. wenn NAA die Origin ablehnt: „not listed in allowed origin“ + SSO 5001 im Runtime-Log).
 * Ohne Timeout bleibt der ganze Autorun bis zum 120-s-Handler-Timeout stehen → keine Signatur.
 */
const SILENT_SSO_TIMEOUT_MS = 8000;

function withSilentSsoTimeout(
  call: Promise<string>,
  allowInteractive: boolean
): Promise<string | null> {
  if (allowInteractive) return call;
  return new Promise<string | null>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      traceLog(`SSO still: kein Callback nach ${SILENT_SSO_TIMEOUT_MS} ms – Fallback`);
      resolve(null);
    }, SILENT_SSO_TIMEOUT_MS);
    call.then(
      (token) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(token);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * OfficeRuntime SSO für die Add-in-API (forMSGraphAccess: false).
 * optional authChallenge: CAE / Kiota-Retry nach 401.
 */
export function getAccessTokenAttempt(
  attempt: number,
  allowInteractive: boolean,
  authChallenge?: string
): Promise<string | null> {
  if (typeof OfficeRuntime === "undefined" || !OfficeRuntime.auth?.getAccessToken) {
    traceLog("getToken: OfficeRuntime.auth fehlt");
    return Promise.resolve(null);
  }
  const opts: OfficeRuntime.AuthOptions = {
    allowSignInPrompt: allowInteractive,
    allowConsentPrompt: allowInteractive,
    forMSGraphAccess: false,
  };
  if (authChallenge) {
    opts.authChallenge = authChallenge;
  }
  const call = withSilentSsoTimeout(
    OfficeRuntime.auth.getAccessToken(opts),
    allowInteractive
  );
  return call.catch((err: unknown) => {
    const e = err as { code?: number | string; errorCode?: number | string; message?: string; name?: string };
    const code = e?.code !== undefined ? e.code : e?.errorCode;
    const msg = String((e?.message || "") + (e?.name || ""));
    const isBusy =
      code === 13008 ||
      code === "13008" ||
      msg.indexOf("13008") >= 0 ||
      (msg.toLowerCase().indexOf("zugriffstoken") >= 0 && msg.toLowerCase().indexOf("bereits") >= 0);
    if (isBusy && attempt < 15) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getAccessTokenAttempt(attempt + 1, allowInteractive, authChallenge));
        }, 350 + attempt * 200);
      });
    }
    /* traceLog statt nur console.warn: im Outlook-Runtime-Log erscheinen nur Log-Einträge. */
    traceLog("SSO Fehler", code ?? "", msg);
    console.warn("[SunnySign] getAccessToken failed:", err);
    return Promise.resolve(null);
  });
}

/** Kiota OfficeAccessTokenProvider ruft bei Bedarf dieselbe OfficeRuntime-SSO-Kette auf (inkl. authChallenge). */
export function getUserToken(allowInteractive: boolean, authChallenge?: string): Promise<string | null> {
  return getAccessTokenAttempt(0, allowInteractive, authChallenge);
}

export type AutorunAccessTokenResult = { token: string | null; usedInteractiveAuth: boolean };

export async function acquireAutorunAccessToken(isMobile: boolean): Promise<AutorunAccessTokenResult> {
  if (isMobile) {
    return new Promise((resolve) => {
      ensureOfficeReady(() => {
        void (async () => {
          traceLog("Mobile: Versuch 1 – SSO still (kein Prompt)");
          let t0 = await getAccessTokenAttempt(0, false);
          if (t0) {
            traceLog("Mobile: SSO still OK");
            resolve({ token: t0, usedInteractiveAuth: false });
            return;
          }
          /*
           * Gespeichertes API-Token (Taskpane-Login) vor interaktivem OfficeRuntime-Prompt nutzen.
           * Android: allowSignInPrompt / Kontoauswahl legt die Compose-Ansicht oft in den Hintergrund
           * (s. OfficeDev/office-js#6052). Ohne gespeichertes Token: interaktiv → ggf. OAuth-Dialog.
           */
          const cached = getStoredToken();
          if (cached) {
            traceLog("Mobile: Token aus Speicher (vor interaktivem Office-Prompt)");
            resolve({ token: cached, usedInteractiveAuth: false });
            return;
          }
          traceLog("Mobile: Versuch 2 – SSO interaktiv (allowSignInPrompt=true)");
          let t1 = await getAccessTokenAttempt(0, true);
          if (t1) {
            traceLog("Mobile: SSO interaktiv OK");
            const pauseMs =
              getOutlookHostName().trim().toLowerCase() === "outlookandroid" ? 700 : 450;
            await new Promise<void>((r) => setTimeout(r, pauseMs));
            resolve({ token: t1, usedInteractiveAuth: true });
            return;
          }
          traceLog("Mobile: Versuch 3 – OAuth-Dialog (Fallback, displayDialogAsync)");
          openMobileOAuthDialogForLaunch((t2) => {
            if (t2) traceLog("Mobile: OAuth-Dialog OK");
            else traceLog("Mobile: OAuth-Dialog fehlgeschlagen – kein Token");
            resolve({ token: t2, usedInteractiveAuth: true });
          });
        })();
      });
    });
  }
  traceLog("Desktop: SSO still (kein Prompt)");
  const token = await getAccessTokenAttempt(0, false);
  traceLog("Desktop SSO still:", token ? "OK" : "kein Token");
  return { token, usedInteractiveAuth: false };
}
